/**
 * A parte do quadro que não desenha nada — e por isso é testada sem navegador.
 *
 * O `<Kanban>` guarda, por coluna, o que já carregou, o cursor da próxima
 * página e se há uma busca em andamento. Tudo que muda esse estado passa por
 * `kanbanReducer`: carregar, mover, desfazer. É função pura de propósito: o
 * movimento otimista e o desfazer são o que mais erra num quadro, e o erro
 * aparece como um cartão duplicado ou sumido, sem exceção nenhuma no console.
 */

import type { StageEvent } from './stageEvents.js';

/**
 * As cores que uma fase pode ter — a paleta fechada de `--cor-fase-*`, no
 * `theme.css`, onde cada uma é medida contra o texto na régua de contraste.
 *
 * O valor é DADO GRAVADO (o módulo guarda `'azul'` na coluna da fase), e por
 * isso fica em português, como todo valor de lista da casa.
 */
export const KANBAN_COLORS = [
  { value: 'cinza', label: 'Cinza' },
  { value: 'azul', label: 'Azul' },
  { value: 'ciano', label: 'Ciano' },
  { value: 'turquesa', label: 'Turquesa' },
  { value: 'verde', label: 'Verde' },
  { value: 'amarelo', label: 'Amarelo' },
  { value: 'laranja', label: 'Laranja' },
  { value: 'vermelho', label: 'Vermelho' },
  { value: 'rosa', label: 'Rosa' },
  { value: 'roxo', label: 'Roxo' },
] as const;

export type KanbanColor = (typeof KANBAN_COLORS)[number]['value'];

/** Quem não escolheu cor tem esta. */
export const DEFAULT_KANBAN_COLOR: KanbanColor = 'cinza';

export interface KanbanStage {
  id: string;
  name: string;
  /** Ausente é `cinza`. */
  color?: KanbanColor;
  /** O que acontece quando um cartão ENTRA nesta fase. Ausente é nenhum. */
  events?: readonly StageEvent[];
  /**
   * Um aviso no cabeçalho da coluna, em vermelho — "3 expirados", o SLA da
   * fase estourado (07/10/2026). Ausente é nenhum: coluna em dia não ganha
   * selo dizendo que está em dia.
   */
  alert?: string;
}

/**
 * O que se escolhe ao criar ou editar uma fase.
 *
 * `events` só vem quando o quadro recebeu `stageEvents` — sem ele, o
 * formulário não mostra a seção e o módulo recebe o mesmo par de sempre.
 */
export interface KanbanStageInput {
  name: string;
  color: KanbanColor;
  events?: StageEvent[];
}

/**
 * Uma página de cartões de UMA coluna.
 *
 * `next` é o cursor opaco da próxima página, ou `null` quando acabou — quem o
 * escolhe é o módulo (deslocamento, id, instante), o quadro só o devolve.
 * `total` é opcional: quando vem, o cabeçalho mostra o número da coluna
 * inteira; quando não vem, mostra quantos carregaram e diz que há mais.
 */
export interface KanbanPage<T> {
  items: T[];
  next: string | null;
  total?: number;
}

/**
 * O movimento, como o módulo o recebe.
 *
 * `beforeId`/`afterId` são os vizinhos do cartão no destino, **entre os que
 * carregaram**. O servidor posiciona relativo a eles: logo depois de
 * `beforeId` quando há um, senão logo antes de `afterId`, senão no topo. Não é
 * o índice que manda — com a coluna paginada, o índice só vale para a parte
 * que a tela viu, e o vizinho vale para a coluna inteira.
 */
export interface KanbanMove {
  cardId: string;
  fromStageId: string;
  toStageId: string;
  /** A posição entre os cartões carregados do destino, já sem o próprio cartão. */
  index: number;
  beforeId: string | null;
  afterId: string | null;
}

export interface KanbanColumn<T> {
  items: T[];
  next: string | null;
  total: number | undefined;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  /** Falso até a primeira página chegar — "vazia" antes disso seria chute. */
  loaded: boolean;
  /**
   * Sobe a cada recomeço da coluna. Resposta que chega com geração antiga é
   * descartada: sem isso, trocar o filtro no meio de uma busca poria a página
   * do filtro velho por cima da lista nova.
   */
  generation: number;
}

export type KanbanColumns<T> = Record<string, KanbanColumn<T>>;

export type KanbanAction<T> =
  | { type: 'sync'; stageIds: readonly string[] }
  | { type: 'reset' }
  | { type: 'loading'; stageId: string; generation: number }
  | {
      type: 'loaded';
      stageId: string;
      generation: number;
      page: KanbanPage<T>;
      /** `true` para a primeira página: substitui em vez de acrescentar. */
      replace: boolean;
    }
  | { type: 'failed'; stageId: string; generation: number; error: string }
  | {
      type: 'move';
      cardId: string;
      fromStageId: string;
      toStageId: string;
      index: number;
    };

export function emptyColumn<T>(generation = 0): KanbanColumn<T> {
  return {
    items: [],
    next: null,
    total: undefined,
    status: 'idle',
    error: null,
    loaded: false,
    generation,
  };
}

/**
 * O reducer precisa saber a identidade do cartão, e o tipo `T` é do módulo —
 * por isso ele nasce de uma fábrica que recebe `cardId`.
 */
export function kanbanReducer<T>(cardId: (card: T) => string) {
  return (state: KanbanColumns<T>, action: KanbanAction<T>): KanbanColumns<T> => {
    switch (action.type) {
      case 'sync': {
        /* Fase nova ganha coluna vazia; fase que saiu leva a coluna junto. A
           que continua fica como está — criar uma fase não recarrega as outras. */
        const next: KanbanColumns<T> = {};
        for (const id of action.stageIds) next[id] = state[id] ?? emptyColumn<T>();
        return next;
      }

      case 'reset': {
        const next: KanbanColumns<T> = {};
        for (const [id, column] of Object.entries(state)) {
          next[id] = emptyColumn<T>(column.generation + 1);
        }
        return next;
      }

      case 'loading': {
        /* Busca que saiu antes de um recomeço não marca a coluna nova como
           "carregando": a resposta dela vai ser descartada, e a coluna ficaria
           esperando para sempre uma página que ninguém mais vai pedir. */
        const column = state[action.stageId];
        if (!column || column.generation !== action.generation) return state;
        return {
          ...state,
          [action.stageId]: { ...column, status: 'loading', error: null },
        };
      }

      case 'loaded': {
        const column = state[action.stageId];
        if (!column || column.generation !== action.generation) return state;
        const incoming = action.replace
          ? action.page.items
          : withoutRepeated(column.items, action.page.items, cardId);
        return {
          ...state,
          [action.stageId]: {
            ...column,
            items: action.replace ? incoming : [...column.items, ...incoming],
            next: action.page.next,
            /* Página seguinte sem total não apaga o que a primeira disse — o
               módulo pode mandar o número só uma vez, e o movimento otimista já
               o vem ajustando. */
            total: action.page.total ?? (action.replace ? undefined : column.total),
            status: 'idle',
            error: null,
            loaded: true,
          },
        };
      }

      case 'failed': {
        const column = state[action.stageId];
        if (!column || column.generation !== action.generation) return state;
        return {
          ...state,
          [action.stageId]: { ...column, status: 'error', error: action.error },
        };
      }

      case 'move':
        return applyMove(state, action, cardId);
    }
  };
}

/**
 * A página seguinte, sem o que a tela já tem.
 *
 * Paginação por deslocamento repete cartão quando alguém move um enquanto a
 * pessoa rola — o cartão que entrou no topo empurra o último da página 1 para
 * a página 2. Duas linhas com a mesma chave no React desenham uma só e
 * embaralham o foco; aqui a segunda simplesmente não entra.
 */
function withoutRepeated<T>(
  current: readonly T[],
  incoming: readonly T[],
  cardId: (card: T) => string,
) {
  const seen = new Set(current.map(cardId));
  return incoming.filter((card) => !seen.has(cardId(card)));
}

/**
 * Tira o cartão da origem e põe no destino, na posição pedida.
 *
 * O total acompanha: sai um de lá e entra um aqui. Quando a coluna não sabe o
 * total (`undefined`), continua não sabendo — inventar um número a partir do
 * que carregou seria afirmar sobre a coluna inteira o que só vale para a
 * parte vista.
 */
export function applyMove<T>(
  state: KanbanColumns<T>,
  move: { cardId: string; fromStageId: string; toStageId: string; index: number },
  cardId: (card: T) => string,
): KanbanColumns<T> {
  const from = state[move.fromStageId];
  const to = state[move.toStageId];
  if (!from || !to) return state;

  const position = from.items.findIndex((c) => cardId(c) === move.cardId);
  if (position === -1) return state;
  const card = from.items[position]!;

  const sourceItems = from.items.filter((_, i) => i !== position);

  if (move.fromStageId === move.toStageId) {
    const items = insertAt(sourceItems, card, move.index);
    return { ...state, [move.fromStageId]: { ...from, items } };
  }

  return {
    ...state,
    [move.fromStageId]: {
      ...from,
      items: sourceItems,
      total: from.total === undefined ? undefined : Math.max(0, from.total - 1),
    },
    [move.toStageId]: {
      ...to,
      items: insertAt(to.items, card, move.index),
      total: to.total === undefined ? undefined : to.total + 1,
    },
  };
}

function insertAt<T>(items: readonly T[], card: T, index: number): T[] {
  const at = Math.max(0, Math.min(index, items.length));
  return [...items.slice(0, at), card, ...items.slice(at)];
}

/**
 * O movimento completo, com os vizinhos — ou `null` quando soltar não muda nada.
 *
 * Soltar o cartão exatamente onde ele estava não é movimento: chamar o módulo
 * nesse caso gravaria um evento "moveu de A para A" e, com servidor lento,
 * piscaria o cartão sem motivo.
 */
export function describeMove<T>(
  state: KanbanColumns<T>,
  move: { cardId: string; fromStageId: string; toStageId: string; index: number },
  cardId: (card: T) => string,
): KanbanMove | null {
  const from = state[move.fromStageId];
  if (!from || !state[move.toStageId]) return null;
  const original = from.items.findIndex((c) => cardId(c) === move.cardId);
  if (original === -1) return null;

  const after = applyMove(state, move, cardId)[move.toStageId]!.items;
  const index = after.findIndex((c) => cardId(c) === move.cardId);
  if (move.fromStageId === move.toStageId && index === original) return null;

  const before = index > 0 ? after[index - 1] : undefined;
  const next = after[index + 1];
  return {
    cardId: move.cardId,
    fromStageId: move.fromStageId,
    toStageId: move.toStageId,
    index,
    beforeId: before === undefined ? null : cardId(before),
    afterId: next === undefined ? null : cardId(next),
  };
}

/**
 * O movimento que desfaz outro, para quando o módulo recusa.
 *
 * É o inverso, e não uma foto das duas colunas de antes: se uma página nova
 * chegou enquanto o servidor pensava, restaurar a foto apagaria essa página.
 */
export function undoMove(move: KanbanMove, originalIndex: number) {
  return {
    cardId: move.cardId,
    fromStageId: move.toStageId,
    toStageId: move.fromStageId,
    index: originalIndex,
  };
}

/**
 * Onde o cartão cai quando é solto sobre OUTRO cartão.
 *
 * Metade de cima do alvo põe antes dele; metade de baixo, depois. O índice é
 * contado já sem o cartão arrastado — soltar logo abaixo de si mesmo na mesma
 * coluna precisa dar a posição em que ele já está, e não uma a mais.
 */
export function dropIndex<T>(
  items: readonly T[],
  overId: string,
  activeId: string,
  below: boolean,
  cardId: (card: T) => string,
): number {
  const rest = items.filter((c) => cardId(c) !== activeId);
  const target = rest.findIndex((c) => cardId(c) === overId);
  if (target === -1) return rest.length;
  return below ? target + 1 : target;
}

/** A nova ordem das fases com uma delas deslocada um passo — ou `null` na ponta. */
export function shiftStage(
  stages: readonly KanbanStage[],
  stageId: string,
  step: -1 | 1,
): string[] | null {
  const ids = stages.map((s) => s.id);
  const at = ids.indexOf(stageId);
  const to = at + step;
  if (at === -1 || to < 0 || to >= ids.length) return null;
  [ids[at], ids[to]] = [ids[to]!, ids[at]!];
  return ids;
}

/**
 * O número do cabeçalho da coluna.
 *
 * Com total, é o total. Sem total e com mais página, é o que carregou seguido
 * de "+": "50+" diz a verdade, e "50" diria que a coluna tem cinquenta.
 */
export function columnCount<T>(column: KanbanColumn<T> | undefined): string | null {
  if (!column?.loaded) return null;
  if (column.total !== undefined) return String(column.total);
  return column.next === null ? String(column.items.length) : `${column.items.length}+`;
}

/**
 * O número da coluna recolhida (28/10/2026). A faixa não carrega cartão: o
 * número vem de uma contagem à parte (`countStage`). Se a coluna já tinha
 * carregado antes de recolher, vale o que ela sabe — é o mesmo recorte.
 */
export function collapsedCount<T>(
  column: KanbanColumn<T> | undefined,
  counted: number | undefined,
): string | null {
  if (column?.loaded) return columnCount(column);
  return counted === undefined ? null : String(counted);
}

/**
 * As colunas recolhidas quando o filtro escolhe uma fase (28/10/2026): a dela
 * aberta, e as outras em faixa — em vez de sumir, como até então. A fase que
 * não é coluna do quadro (um desfecho) não recolhe nada.
 */
export function collapsedForFocus(stageIds: readonly string[], focus: string): string[] {
  if (!stageIds.includes(focus)) return [];
  return stageIds.filter((id) => id !== focus);
}

/** A lista de recolhidas depois de um clique — sem repetir e sem perder a ordem. */
export function toggleCollapsed(
  list: readonly string[],
  stageId: string,
  collapsed: boolean,
): string[] {
  if (collapsed) return list.includes(stageId) ? [...list] : [...list, stageId];
  return list.filter((id) => id !== stageId);
}

/**
 * O percentual da barra de progresso do cartão, inteiro entre 0 e 100.
 *
 * Zero de zero é 0%, e não `NaN%`: cartão sem subtarefa não está "pronto".
 * Arredonda para baixo — 99,6% escrito como 100% diria que acabou.
 */
export function progressPercent(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor((done / total) * 100)));
}
